import {
  CopyOutlined,
  FormOutlined,
  InfoCircleFilled,
  MailOutlined,
  MessageOutlined,
} from "@ant-design/icons";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Popover,
  Row,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import { FormInstance } from "antd/es/form/Form";
import copy from "copy-to-clipboard";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";

import {
  getDiscordMemberChannelCounts,
  getDiscordMemberRecentActivity,
} from "../../lib/discordQueries";
import {
  AllMembers,
  AtRiskMember,
  DiscordMemberChannelCounts,
  DiscordMemberRpc,
  DiscordRecentMemberActivity,
} from "../../lib/discordTypes";
import {
  loadDirectoryProfile,
  memberChannelCounts,
  memberRecentActivity,
} from "../../lib/queries";
import { getSupaClient, getWorkspace } from "../../lib/supabase";
import { timeSince, timeSinceUnformatted } from "../../utils/general";
import DateRangePicker from "../DateRangePicker/DateRangePicker";
import MemberTags from "../MemberTags/MemberTags";
import SendDM from "../SendDM/SendDM";
import AdminEditProfile from "./AdminEditProfile";

const { TabPane } = Tabs;
const { TextArea } = Input;

const dateFormat = "MM/DD/YY";

interface UserDetail {
  userInfo: any;
  recentActivity: any;
  userChannelCounts: DiscordMemberChannelCounts[] | null;
  directoryProfile?: { slack_id: any };
}

interface Props {
  platform?: "slack" | "discord";
  discordUser?: AllMembers | AtRiskMember | DiscordMemberRpc | null;
  slackUser?: { slack_id: any };
  onTeamTagAdded?: any;
  onMemberTagAdded?: any;
  onMemberTagRemoved?: any;
}

/**
 * MemberDetail typescript component
 * @param record
 * @param platform
 * @param onTeamTagAdded - a function to callback in the parent component if team tags are updated
 * @param onMemberTagAdded: a function to callback in the parent component if member tags are updated
 * @param onMemberTagRemoved: a function to callback in the parent component if member tags are removed
 */

function MemberDetail({
  discordUser,
  slackUser,
  platform: platformProp,
  onTeamTagAdded,
  onMemberTagAdded,
  onMemberTagRemoved,
}: Props) {
  const platform = platformProp
    ? platformProp
    : localStorage.getItem("platform");
  const supabase = getSupaClient();
  const workspace = getWorkspace(supabase);
  const [detailUser, setDetailUser] = useState<UserDetail>({
    userInfo: null,
    recentActivity: null,
    userChannelCounts: null,
    directoryProfile: { slack_id: null },
  });

  const [isUserDetailLoading, setIsUserDetailLoading] = useState(true);
  const [allNotes, setAllNotes] = useState<any[]>([]);
  const formRef = useRef<FormInstance<any>>(null);

  const [startDate, setStartDate] = useState(
    moment().subtract(7, "days").format(dateFormat)
  );
  const [endDate, setEndDate] = useState(moment().format(dateFormat));

  const slackId = localStorage.getItem("slack-id");
  const teamId =
    platform === "slack"
      ? localStorage.getItem("team-id")
      : localStorage.getItem("guild-id");

  const userChannelCountColumns = [
    {
      title: "Channel",
      dataIndex: "channel_name",
      key: "channel_name",
      width: "20%",
      sorter: (a: DiscordMemberChannelCounts, b: DiscordMemberChannelCounts) =>
        a.channel_name.localeCompare(b.channel_name),
    },
    {
      title: "# conversations started",
      dataIndex: "parent_count",
      key: "parent_count",
      sorter: (
        a: DiscordMemberChannelCounts,
        b: DiscordMemberChannelCounts
      ) => {
        return a.parent_count - b.parent_count;
      },
      width: "20%",
    },
    {
      title: "# reactions added",
      dataIndex: "reaction_count",
      sorter: (
        a: DiscordMemberChannelCounts,
        b: DiscordMemberChannelCounts
      ) => {
        return a.reaction_count - b.reaction_count;
      },
      width: "20%",
    },
    {
      title: "# thread msgs",
      dataIndex: "thread_count",
      key: "thread_count",
      sorter: (
        a: DiscordMemberChannelCounts,
        b: DiscordMemberChannelCounts
      ) => {
        return a.thread_count - b.thread_count;
      },
      width: "20%",
    },
    {
      title: "Total actions taken",
      dataIndex: "total_count",
      key: "total_count",
      sorter: (
        a: DiscordMemberChannelCounts,
        b: DiscordMemberChannelCounts
      ) => {
        return a.total_count - b.total_count;
      },
      width: "20%",
    },
  ];

  const recentActivityColumns = [
    {
      title: "Posted",
      dataIndex: "ageText",
      sorter: (
        a: DiscordRecentMemberActivity,
        b: DiscordRecentMemberActivity
      ) => {
        return a.ageSeconds - b.ageSeconds;
      },
      width: "20%",
    },
    {
      title: "Channel",
      dataIndex: "channel_name",
      width: "20%",
    },
    {
      title: "Message",
      dataIndex: "msg_content",
      width: "60%",
      render: (_text: string, record: DiscordRecentMemberActivity) => {
        return (
          <Space size="middle">
            <a href={record.permalink} target="_blank" rel="noreferrer">
              {record.msg_content}
            </a>
          </Space>
        );
      },
    },
  ];

  const allNotesColumns = [
    {
      title: "Added",
      dataIndex: "ageText",
      width: "20%",
    },
    {
      title: "Note",
      dataIndex: "text",
      width: "60%",
    },
  ];

  // Loads notes if slack user
  const loadNotes = async () => {
    if (platform === "slack" && slackUser) {
      const { data, error } = await supabase
        .from("user_notes")
        .select("*")
        .match({
          slack_user: slackUser.slack_id,
          team: teamId,
        })
        .order("created_at", { ascending: false });
      if (data) {
        const recentNotes = data.map((result) => {
          const timeSincePosted = timeSinceUnformatted(result.created_at);

          result.ageText = timeSincePosted;
          return result;
        });
        setAllNotes(recentNotes);
      }
      if (error) {
        console.log(error);
      }
    }
  };

  const loadData = async () => {
    let userChannelCounts: DiscordMemberChannelCounts[] | null = [];
    let recentActivity: any[] = [];
    let directoryProfile: any = null;
    if (platform === "slack" && slackUser) {
      // pull slack recent activity and channel counts
      const resMemberRecentActivity = await memberRecentActivity(
        startDate,
        endDate,
        slackUser.slack_id
      );
      recentActivity = resMemberRecentActivity ? resMemberRecentActivity : [];
      userChannelCounts = await memberChannelCounts(
        startDate,
        endDate,
        teamId,
        slackUser.slack_id
      );
      directoryProfile = await loadDirectoryProfile(slackUser.slack_id);
    }

    if (platform === "discord" && discordUser) {
      // pull discord recent activity and channel counts
      const resGetDiscordMemberRecentActivity =
        await getDiscordMemberRecentActivity(
          startDate,
          endDate,
          discordUser.id
        );
      recentActivity = resGetDiscordMemberRecentActivity
        ? resGetDiscordMemberRecentActivity
        : [];
      userChannelCounts = await getDiscordMemberChannelCounts(
        startDate,
        endDate,
        teamId as string,
        discordUser.id
      );
    }

    recentActivity = recentActivity.map((result) => {
      const timeSincePosted = timeSince(result.ts);
      result.ageText = timeSincePosted.text;
      result.ageSeconds = timeSincePosted.seconds;
      return result;
    });

    setDetailUser({
      userInfo: slackUser || discordUser,
      recentActivity,
      userChannelCounts,
      directoryProfile,
    });
    setIsUserDetailLoading(false);
  };

  useEffect(() => {
    setIsUserDetailLoading(true);
    loadData();
    loadNotes();
  }, [discordUser, slackUser, startDate, endDate]);

  // note
  const noteFinish = async (values: { note: string }) => {
    const { data, error } = await supabase.from("user_notes").insert({
      slack_user: detailUser.userInfo.slack_id,
      text: values.note,
      written_by: slackId,
      team: teamId,
      created_at: new Date(),
      workspace,
    });
    if (data && formRef && formRef.current) {
      loadNotes();
      formRef.current.resetFields();
      message.info("Note saved!");
    }
    if (error) {
      message.info("Error saving notes");
    }
  };

  if (!slackUser && !discordUser) {
    return <Spin />;
  }

  // Function to provide for admin edit component to load the updated profile
  const updateProfileForAdminEdit = async (updateSlackId: number) => {
    const directoryProfile = await loadDirectoryProfile(updateSlackId);
    setDetailUser({
      ...detailUser,
      directoryProfile,
    });
  };

  let profileImg = "";
  if (platform === "discord" && discordUser && discordUser.avatar) {
    profileImg = `https://cdn.discordapp.com/avatars/${discordUser.discord_id}/${discordUser.avatar}.png`;
  } else {
    profileImg = detailUser.userInfo?.image_512
      ? detailUser.userInfo?.image_512
      : "/avatar_slack_128.png";
  }
  return (
    <div style={{ padding: 10 }}>
      <Spin tip="😃 Loading..." spinning={isUserDetailLoading}>
        <Row gutter={[32, 48]} justify="center" align="top">
          <Col
            lg={5}
            md={12}
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              style={{ borderRadius: 20 }}
              alt="user"
              width="128"
              src={profileImg}
            />
            {platform === "discord" && discordUser ? (
              <>
                <Popover content="Copied!" trigger="click">
                  <Button
                    type="link"
                    onClick={() => {
                      copy(discordUser.discord_id);
                    }}
                  >
                    <CopyOutlined />
                    Copy ID
                  </Button>
                </Popover>
                <Popover content="Copied!" trigger="click">
                  <Button
                    type="link"
                    onClick={() => {
                      copy(
                        `${discordUser.username}#${discordUser.discriminator}`
                      );
                    }}
                  >
                    <CopyOutlined />
                    Copy username
                  </Button>
                </Popover>
              </>
            ) : (
              ""
            )}
          </Col>
          <Col lg={5} md={12}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignContent: "center",
                justifyContent: "space-between",
              }}
            >
              Tags
              <MemberTags
                member={discordUser || slackUser}
                memberId={
                  platform === "discord" && discordUser
                    ? discordUser.id
                    : platform === "slack" && slackUser
                    ? slackUser.slack_id
                    : null
                }
                onTeamTagAdded={onTeamTagAdded}
                onMemberTagAdded={onMemberTagAdded}
                onMemberTagRemoved={onMemberTagRemoved}
              />
              <br />
              <div className="mt-4">
                {discordUser &&
                  discordUser.discord_roles?.map((role) => (
                    <Tag style={{ marginBottom: 6 }}>{role.name}</Tag>
                  ))}
              </div>
              <div>
                {detailUser.userInfo?.email ? (
                  <a href={`mailto:${detailUser.userInfo.email}`}>
                    <Button type="primary" icon={<MailOutlined />}>
                      Send an email
                    </Button>
                  </a>
                ) : null}
              </div>
            </div>
          </Col>
          <Col span={16} lg={14} md={24}>
            <Tabs defaultActiveKey="note">
              <TabPane
                tab={
                  <span>
                    {" "}
                    <FormOutlined />
                    Add a quick note
                  </span>
                }
                key="note"
                disabled={platform !== "slack"}
              >
                <Form name="control-ref" ref={formRef} onFinish={noteFinish}>
                  <Form.Item name="note" rules={[{ required: true }]}>
                    {platform === "slack" ? (
                      <TextArea
                        placeholder="Enter a private note, only visible to your team"
                        rows={4}
                      />
                    ) : (
                      "Coming soon!"
                    )}
                  </Form.Item>

                  <Form.Item>
                    <Button
                      type="primary"
                      htmlType="submit"
                      disabled={platform !== "slack"}
                    >
                      Save
                    </Button>
                  </Form.Item>
                </Form>
              </TabPane>
              <TabPane
                tab={
                  <span>
                    {" "}
                    <MessageOutlined />
                    Send /Schedule DM
                  </span>
                }
                key="dms"
                disabled={platform !== "slack"}
              >
                <SendDM
                  sendNow
                  hideSelectMembers
                  selectedUsers={
                    platform === "slack" && [detailUser.userInfo?.slack_id]
                  }
                />
              </TabPane>
            </Tabs>
          </Col>
        </Row>

        <Row gutter={[48, 48]} style={{ marginTop: 30 }}>
          <Col lg={24} md={24}>
            <Tabs
              defaultActiveKey="channel"
              tabBarExtraContent={
                <div className="flex gap-3 items-center justify-center">
                  {platform === "discord" && (
                    <Tooltip title="We limit messages to 30 days to comply with Discord policies">
                      <InfoCircleFilled className="text-base opacity-70" />
                    </Tooltip>
                  )}
                  <DateRangePicker
                    endDate={endDate}
                    setEndDate={setEndDate}
                    startDate={startDate}
                    setStartDate={setStartDate}
                    isIndependentDateSelector
                    customOption={{ disabled: false }}
                    rangeOfSelector={{
                      range: "day",
                      selectorDefaultOptions: [
                        { value: 7, disabled: false },
                        {
                          value: 30,
                          disabled: false,
                        },
                      ],
                    }}
                  />
                </div>
              }
            >
              {detailUser.userChannelCounts ? (
                <TabPane tab="Channel activity" key="channel">
                  <div style={{ width: "100%", height: "100%" }}>
                    <Table
                      dataSource={detailUser.userChannelCounts}
                      columns={userChannelCountColumns}
                    />
                  </div>
                </TabPane>
              ) : null}
              <TabPane tab="Recent messages" key="recent">
                <div style={{ width: "100%", height: "100%" }}>
                  <Table
                    dataSource={detailUser.recentActivity}
                    columns={recentActivityColumns}
                  />
                </div>
              </TabPane>
              <TabPane
                tab="Edit directory profile"
                key="directory"
                disabled={platform !== "slack"}
              >
                <div style={{ width: "100%", height: "100%" }}>
                  {detailUser.directoryProfile ? (
                    <AdminEditProfile
                      profile={detailUser.directoryProfile}
                      updateProfile={() =>
                        updateProfileForAdminEdit(
                          detailUser.directoryProfile?.slack_id
                        )
                      }
                    />
                  ) : (
                    "Member directory not set. Please contact us to set it up!"
                  )}
                </div>
              </TabPane>
              <TabPane tab="Notes" key="notes" disabled={platform !== "slack"}>
                <div style={{ width: "100%", height: "100%" }}>
                  <Table dataSource={allNotes} columns={allNotesColumns} />
                </div>
              </TabPane>
            </Tabs>
          </Col>
        </Row>
      </Spin>
    </div>
  );
}

MemberDetail.defaultProps = {
  platform: null,
  onTeamTagAdded: () => {},
  onMemberTagAdded: () => {},
  onMemberTagRemoved: () => {},
  discordUser: null,
  slackUser: null,
};

export default MemberDetail;
